import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, FileText, PlayCircle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function TrainingCourse() {
  const { cid } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const [phase, setPhase] = useState("modules"); // modules | assessment
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/applicator/training/${cid}`).then(({ data }) => { setData(data); setOpenModule((m) => m ?? data.course.modules[0]?.id); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  if (!data) return <div className="text-gray-500">Loading…</div>;
  const { course } = data;
  const done = new Set(data.completed_modules || []);
  const allDone = course.modules.every((m) => done.has(m.id));

  const start = async () => { await api.post(`/applicator/training/${cid}/start`); toast.success(t("start_training")); load(); };
  const markDone = async (mid) => {
    await api.post(`/applicator/training/${cid}/module/${mid}/complete`);
    const idx = course.modules.findIndex((m) => m.id === mid);
    if (idx < course.modules.length - 1) setOpenModule(course.modules[idx + 1].id);
    load();
  };
  const openAssessment = async () => {
    const { data } = await api.get(`/applicator/training/${cid}/assessment`);
    setAssessment(data); setResult(null); setAnswers({}); setPhase("assessment");
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/applicator/training/${cid}/assessment/submit`, { answers });
      setResult(data);
      if (data.passed) toast.success(`${t("passed")} · ${data.score}%`); else toast.error(`${t("failed")} · ${data.score}%`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    setSubmitting(false);
  };

  return (
    <div data-testid="training-course-page">
      <button onClick={() => navigate("/applicator/training")} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5 mb-4"><ArrowLeft size={15} /> {t("back_to_training")}</button>
      <h1 className="serif-head text-2xl lg:text-3xl">{course.title}</h1>
      <p className="text-xs text-gray-500 mt-1">{course.category} · {course.module_count} {t("module")}s · {t("cert_status") || "Status"}: <span className="gold-text">{data.cert_status}</span></p>
      <p className="text-[11px] text-gray-600 mt-2 mb-6">{course.placeholder}</p>

      {phase === "modules" && (
        <>
          {data.status === "NOT STARTED" && (
            <button onClick={start} data-testid="start-training-btn" className="mb-6 bg-[#C9A45C] text-black px-6 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors">{t("start_training")}</button>
          )}
          <div className="space-y-2" data-testid="module-list">
            {course.modules.map((m, i) => {
              const isDone = done.has(m.id);
              const isOpen = openModule === m.id;
              return (
                <div key={m.id} className="hair-border bg-white/[0.02]" data-testid={`module-${m.id}`}>
                  <button onClick={() => setOpenModule(isOpen ? null : m.id)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] shrink-0 ${isDone ? "bg-emerald-500 text-black" : "border border-white/20 text-gray-400"}`}>{isDone ? <Check size={13} /> : i + 1}</span>
                    <span className="text-sm flex-1">{m.title}</span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/5">
                      {m.image && <img src={m.image} alt={m.title} className="w-full max-h-52 object-cover rounded mb-3" />}
                      <p className="text-sm text-gray-300 whitespace-pre-line leading-relaxed">{m.content}</p>
                      <div className="flex flex-wrap gap-3 mt-3">
                        {m.video && <a href={m.video} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1.5 text-[#C9A45C] hover:underline"><PlayCircle size={14} /> {t("watch_video")}</a>}
                        {m.pdf && <a href={m.pdf} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1.5 text-[#C9A45C] hover:underline"><FileText size={14} /> {t("download_pdf")}</a>}
                      </div>
                      {!isDone && data.status !== "NOT STARTED" && (
                        <button onClick={() => markDone(m.id)} data-testid={`complete-module-${m.id}`} className="mt-4 border border-white/20 px-4 py-2 text-xs hover:bg-white hover:text-black transition-colors">{t("mark_complete")}</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {allDone && data.cert_status !== "CERTIFIED" && (
            <button onClick={openAssessment} data-testid="take-assessment-btn" className="mt-6 bg-[#C9A45C] text-black px-6 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors">{t("take_assessment")}</button>
          )}
          {data.cert_status === "CERTIFIED" && (
            <div className="mt-6 hair-border bg-emerald-500/10 border-emerald-500/40 p-4 text-sm text-emerald-300" data-testid="course-certified">You are CERTIFIED for this service.</div>
          )}
          {data.cert_status === "AWAITING CERTIFICATION" && (
            <div className="mt-6 hair-border bg-[#C9A45C]/10 border-[#C9A45C]/40 p-4 text-sm text-[#E4C987]">{t("awaiting_certification")} — ERRĀH admin will approve your certification shortly.</div>
          )}
        </>
      )}

      {phase === "assessment" && assessment && (
        <div data-testid="assessment-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl">{t("assessment")} — {assessment.service_name}</h2>
            <button onClick={() => setPhase("modules")} className="text-xs text-gray-400 hover:text-white">✕</button>
          </div>
          {result ? (
            <div className={`hair-border p-6 text-center ${result.passed ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"}`} data-testid="assessment-result">
              {result.passed ? <CheckCircle2 className="mx-auto text-emerald-400" size={40} /> : <XCircle className="mx-auto text-red-400" size={40} />}
              <p className="font-display text-2xl mt-3">{result.passed ? t("passed") : t("failed")}</p>
              <p className="text-sm text-gray-400 mt-1">{t("score")}: {result.score}% ({result.correct}/{result.total})</p>
              {result.cert_status === "CERTIFIED" && <p className="text-emerald-300 text-sm mt-3">You are now CERTIFIED.</p>}
              {result.cert_status === "AWAITING CERTIFICATION" && <p className="text-[#E4C987] text-sm mt-3">{t("awaiting_certification")} — awaiting admin approval.</p>}
              <div className="mt-4 flex gap-3 justify-center">
                <button onClick={() => navigate("/applicator/certification")} className="border border-white/20 px-4 py-2 text-xs hover:bg-white hover:text-black">{t("certification")}</button>
                {!result.passed && <button onClick={openAssessment} data-testid="retake-btn" className="bg-[#C9A45C] text-black px-4 py-2 text-xs font-semibold">{t("retake_assessment")}</button>}
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-4">Pass mark {assessment.pass_percentage}% · Attempts used {assessment.attempts_used}/{assessment.max_attempts}</p>
              <div className="space-y-5">
                {assessment.questions.map((q, qi) => (
                  <div key={q.id} className="hair-border bg-white/[0.02] p-4" data-testid={`question-${q.id}`}>
                    <p className="text-sm mb-3">{qi + 1}. {q.q}</p>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => (
                        <label key={oi} className={`flex items-center gap-2.5 text-sm px-3 py-2 border cursor-pointer transition-colors ${answers[q.id] === oi ? "border-[#C9A45C] bg-[#C9A45C]/10 text-white" : "border-white/10 text-gray-400 hover:border-white/25"}`}>
                          <input type="radio" name={q.id} checked={answers[q.id] === oi} onChange={() => setAnswers((a) => ({ ...a, [q.id]: oi }))} data-testid={`opt-${q.id}-${oi}`} className="accent-[#C9A45C]" />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={submit} disabled={submitting || Object.keys(answers).length < assessment.questions.length}
                data-testid="submit-assessment-btn"
                className="mt-6 bg-[#C9A45C] text-black px-6 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-40">{t("submit_assessment")}</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
